import {
  Box, Flex, HStack, Image, Text, Stack
} from "@chakra-ui/react";
import { HashLink } from "react-router-hash-link";
import smoothscroll from "smoothscroll-polyfill";
import { useEffect, useState } from "react";
import { Menu, X } from "lucide-react";

const logo = process.env.PUBLIC_URL + "/logos/31Gauge_Horizontal.svg";

const Links = [
  { navName: "How It Works", navId: "/#how-it-works" },
  { navName: "MCQs", navId: "/#mcqs" },
  { navName: "Pricing", navId: "/#pricing" },
  { navName: "Contact", navId: "/#contact" },
];

export const Navbar = () => {
  smoothscroll.polyfill();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 72);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navBg = scrolled
    ? "rgba(255,255,255,0.95)"
    : "transparent";

  const linkColor = scrolled ? "#293452" : "rgba(255,255,255,0.88)";
  const linkHover = scrolled ? "#0095C8" : "white";

  return (
    <>
      <Box
        px={{ base: "5", md: "8", lg: "10" }}
        position="fixed"
        top={0}
        w="100%"
        zIndex={1000}
        bg={navBg}
        borderBottom={scrolled ? "1px solid rgba(41,52,82,0.08)" : "none"}
        sx={{ backdropFilter: scrolled ? "blur(20px)" : "none" }}
        boxShadow={scrolled ? "0 2px 20px rgba(41,52,82,0.08)" : "none"}
        transition="background 0.3s ease, box-shadow 0.3s ease"
      >
        <Flex
          h="72px"
          align="center"
          justify="space-between"
        >
          {/* Logo */}
          <HashLink smooth to="/#header">
            <Image
              h={{ base: "30px", md: "34px", lg: "38px" }}
              src={logo}
              alt="31Gauge"
              filter={scrolled ? "none" : "brightness(0) invert(1)"}
              transition="filter 0.3s ease"
            />
          </HashLink>

          {/* Desktop nav — centered */}
          <HStack
            as="nav"
            spacing="8"
            display={{ base: "none", md: "flex" }}
            position="absolute"
            left="50%"
            transform="translateX(-50%)"
          >
            {Links.map(({ navName, navId }) => (
              <HashLink smooth to={navId} key={navId}>
                <Text
                  fontFamily="'Inter', sans-serif"
                  fontWeight="500"
                  fontSize="0.9375rem"
                  color={linkColor}
                  _hover={{ color: linkHover }}
                  transition="color 0.2s"
                  whiteSpace="nowrap"
                >
                  {navName}
                </Text>
              </HashLink>
            ))}
          </HStack>

          {/* Desktop CTA */}
          <Box display={{ base: "none", md: "block" }}>
            <HashLink smooth to="/#download-app">
              <Box
                as="button"
                px="24px"
                py="10px"
                borderRadius="8px"
                background="linear-gradient(135deg, #0095C8, #016293)"
                color="white"
                fontFamily="'Public Sans', sans-serif"
                fontWeight="600"
                fontSize="0.9rem"
                letterSpacing="0.02em"
                boxShadow="0 4px 16px rgba(0,149,200,0.3)"
                _hover={{
                  transform: "translateY(-2px)",
                  boxShadow: "0 8px 24px rgba(0,149,200,0.4)",
                }}
                transition="all 0.25s cubic-bezier(0.4,0,0.2,1)"
              >
                Start Free
              </Box>
            </HashLink>
          </Box>

          {/* Mobile hamburger */}
          <Box
            display={{ base: "flex", md: "none" }}
            as="button"
            onClick={() => setMobileOpen(!mobileOpen)}
            color={scrolled ? "#293452" : "white"}
            p={2}
            aria-label="Toggle menu"
          >
            {mobileOpen ? <X size={24} /> : <Menu size={24} />}
          </Box>
        </Flex>
      </Box>

      {/* Mobile full-screen drawer */}
      {mobileOpen && (
        <Box
          position="fixed"
          inset={0}
          bg="#293452"
          zIndex={999}
          display={{ md: "none" }}
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          pt="72px"
        >
          {/* Close button */}
          <Box
            position="absolute"
            top="20px"
            right="20px"
            as="button"
            onClick={() => setMobileOpen(false)}
            color="white"
            p={2}
          >
            <X size={24} />
          </Box>

          <Stack spacing={8} align="center" mt={8}>
            {Links.map(({ navName, navId }) => (
              <HashLink
                smooth
                to={navId}
                key={navId}
                onClick={() => setMobileOpen(false)}
              >
                <Text
                  fontFamily="'Public Sans', sans-serif"
                  fontWeight="600"
                  fontSize="1.5rem"
                  color="rgba(255,255,255,0.88)"
                  _hover={{ color: "#0095C8" }}
                  minH="48px"
                  display="flex"
                  alignItems="center"
                >
                  {navName}
                </Text>
              </HashLink>
            ))}
            <HashLink smooth to="/#download-app" onClick={() => setMobileOpen(false)}>
              <Box
                as="button"
                px="40px"
                py="14px"
                borderRadius="8px"
                background="linear-gradient(135deg, #0095C8, #016293)"
                color="white"
                fontFamily="'Public Sans', sans-serif"
                fontWeight="600"
                fontSize="1rem"
                mt={4}
                boxShadow="0 4px 16px rgba(0,149,200,0.3)"
              >
                Start Free
              </Box>
            </HashLink>
          </Stack>
        </Box>
      )}
    </>
  );
};
